// Generador de ZPL II para etiquetas de composición/cuidado (Zebra GT800, 203 dpi).
// - QR nativo (^BQN), UTF-8 (^CI28), word-wrap (^FB).
// - Layout de FILA: 'columnas' etiquetas dentro de un mismo ^XA...^XZ (un avance de papel).
// - Todo parametrizado en CONFIG (no hardcodear medidas).
//
// ⚠️ MEDIR EL ROLLO FÍSICO Y AJUSTAR ESTAS MEDIDAS (con calibrador):
//    3 x 35 mm = 105 mm > 104 mm imprimibles, así que 35 mm NO cabe a 3-across.
//    Estos valores son un punto de partida que SÍ cabe; corrígelos con el rollo real
//    y verifícalo en labelary.com antes de quemar rollo.
export const CONFIG_ETIQUETA_ZEBRA = {
  dpi: 203,               // GT800 estándar = 203 dpi = 8 dots/mm
  columnas: 3,            // etiquetas por fila (rollo 3 en línea)
  anchoEtiquetaMm: 33,    // ancho de CADA etiqueta  (confirmado 33)
  altoEtiquetaMm: 55,     // alto de la etiqueta      (confirmado 55)
  gapHorizontalMm: 2,     // separación entre columnas (CONFIRMAR físico; 99+4+0.5=103.5<=104 ✓)
  margenMm: 0.5,          // dónde empieza la 1ª columna (CONFIRMAR físico)
  // Impresión: se fijan desde el ZPL para no depender de lo que tenga guardado la impresora.
  oscuridad: 20,          // ~SD 0..30. Alto = letra gruesa y cabezal caliente. Subir si sale tenue (más de ~22 empasta el QR).
  velocidadIps: 2,        // ^PR pulgadas/seg. 2 = lo más lento de la GT800: más tiempo de calor por línea, QR más nítido.
  mediaConGap: true,      // ^MNY: sincroniza con el hueco entre etiquetas (evita que se encimen)
  // QR: payload corto (qrPayload) => versión 1-2 del QR. La magnificación se calcula por
  // etiqueta: la más grande que quepa, con este tope. Versión 1 => mag 7 (cuadrito 0.875 mm,
  // QR ~18 mm); versión 2 => mag 6 (0.75 mm). Antes era mag 5 fija (0.625 mm) y la impresora,
  // que no imprime perfecto, lo dejaba ilegible.
  qrMag: 7,               // magnificación MÁXIMA del módulo del QR
  qrMinTextoDots: 85,     // ancho mínimo que se deja a la derecha para modelo/talla/color (~10.5 mm)
};

// Módulos por lado del QR que va a generar la Zebra (ECC L): versión 1 = 21, versión 2 = 25...
// Así se sabe cuánto mide y se le da la magnificación más grande que quepa.
const QR_ALFANUMERICO = /^[0-9A-Z $%*+\-./:]*$/;
const QR_CAPACIDAD_L = {
  alfanumerico: [25, 47, 77, 114, 154, 195],
  bytes: [17, 32, 53, 78, 106, 134],
};
function modulosQR(texto) {
  const caps = QR_ALFANUMERICO.test(texto) ? QR_CAPACIDAD_L.alfanumerico : QR_CAPACIDAD_L.bytes;
  const i = caps.findIndex((c) => texto.length <= c);
  const version = i === -1 ? caps.length + 1 : i + 1;
  return 17 + 4 * version;
}

// Bloque fijo de marca/cuidado (los acentos se quitan al generar el ZPL).
export const ETIQUETA_MARCA_ZPL = {
  hechoPor: "HECHO EN MÉXICO POR",
  razonSocial: "COSECHANDO BENDICIONES Y EXITOS SA DE C.V",
  rfc: "RFC: CBE260511FGA",
  domicilio: "Mesones #121 1er piso esq. Pino Suárez, Col. Centro CP 06090, CD de México",
  telefono: "Haga sus pedidos al 55 5524 1844",
  mayoreo: "Mayoreo y medio mayoreo",
  mensaje:
    "Nosotros los fabricantes te deseamos que cuando uses esta prenda la luz del CREADOR te acompañe para hacer el bien con tu semejante",
  composicion: "COMP: 100% POLIÉSTER",
  marca: "gavini",
};

const mm2dots = (mm, dpi) => Math.round((mm * dpi) / 25.4);

// Contenido del QR de prenda: "CODIGO$COLOR$TALLA" en MAYÚSCULAS sin acentos. Así cabe en el
// modo alfanumérico del QR (el más compacto) y el QR sale con cuadritos más grandes.
// "$" está en la misma tecla en teclado US y español (el "|" llegaba como "]" o "}").
// VentaDirecta acepta este formato y el viejo con "|".
export const qrPayload = (codigo, color, talla) =>
  [codigo, color, talla]
    .map((v) =>
      String(v == null ? "" : v)
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toUpperCase()
        .replace(/\$/g, "")
        .trim()
    )
    .join("$");
// Quita acentos/diacríticos (É→E, ñ→n): las Zebra viejas / en modo EPL no soportan
// UTF-8 y los imprimen en blanco ("M XICO").
const sinAcentos = (t) =>
  String(t == null ? "" : t)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
// Escapa los caracteres de control de ZPL en el texto de datos (^, ~, \) y quita acentos
const esc = (t) => sinAcentos(t).replace(/([\^~\\])/g, "\\$1");

// ¿Cuántas líneas ocupa 'texto' con word-wrap en un ancho de 'cols' caracteres?
function lineasQueOcupa(texto, cols) {
  if (cols < 1) return Infinity;
  const palabras = String(texto || "").split(/\s+/).filter(Boolean);
  if (!palabras.length) return 1;
  let lineas = 1;
  let largo = 0;
  for (const p of palabras) {
    if (p.length > cols) return Infinity; // una palabra sola no cabe
    if (largo === 0) largo = p.length;
    else if (largo + 1 + p.length <= cols) largo += 1 + p.length;
    else { lineas++; largo = p.length; }
  }
  return lineas;
}

// Busca el tamaño de fuente más grande con el que 'texto' cabe en 'anchoDots'
// usando como máximo 'maxLineas'. Evita que ^FB imprima el sobrante ENCIMA
// de la primera línea (que es lo que hacía salir el modelo empalmado).
// La fuente 0 de ZPL es PROPORCIONAL: el ancho real promedio de un carácter es
// ~0.55 de su alto (no el 0.85 que se declara en ^A0N, que es el tope de la celda).
const FACTOR_ANCHO = 0.55;
function tamanoQueCabe(texto, anchoDots, maxLineas = 1, maxSize = 30, minSize = 8) {
  const t = sinAcentos(texto || "");
  for (let size = maxSize; size >= minSize; size--) {
    const cols = Math.floor(anchoDots / (size * FACTOR_ANCHO));
    if (lineasQueOcupa(t, cols) <= maxLineas) return size;
  }
  return minSize;
}

// Dibuja UNA etiqueta con el origen desplazado a (ox) dots. Devuelve el ZPL de esa columna.
function columnaZPL(reg, ox, anchoDots, altoDots, cfg = CONFIG_ETIQUETA_ZEBRA) {
  const M = ETIQUETA_MARCA_ZPL;
  const pad = mm2dots(1.5, 203); // margen interno ~1.5mm (se recalcula por dpi abajo si hace falta)
  const w = anchoDots - pad * 2; // ancho útil de texto
  const x = ox + pad;
  let y = pad;
  const out = [];

  // Helper de línea con word-wrap (^FB): size = alto de fuente en dots, maxLineas.
  // Si el texto no cabe con ese tamaño, se reduce para que no se empalme.
  const linea = (texto, size, maxLineas = 1, align = "L") => {
    const s = Math.min(size, tamanoQueCabe(texto, w, maxLineas, size));
    out.push(`^FO${x},${y}`);
    out.push(`^A0N,${s},${Math.round(s * 0.85)}`);
    out.push(`^FB${w},${maxLineas},0,${align},0`);
    out.push(`^FD${esc(texto)}^FS`);
    // el alto reservado no cambia; separación corta entre bloques para dejarle más alto al QR
    y += size * maxLineas + Math.round(size * 0.15);
  };

  // === Bloque de marca (arriba) ===
  linea(M.hechoPor, 18, 1);
  linea(M.razonSocial, 18, 2);
  linea(M.rfc, 16, 1);
  linea(M.domicilio, 15, 3);
  linea(M.telefono, 16, 1);
  linea(M.mayoreo, 15, 1);
  linea(M.mensaje, 15, 4);
  linea(reg.composicion || M.composicion, 18, 1);

  // === QR nativo (abajo-izquierda) + Modelo/Talla a su derecha ===
  const qrData = reg.qr || `${reg.modelo || ""}|${reg.talla || ""}`;
  const qrY = y + 2;
  // Magnificación: la más grande que quepa en el alto que queda (^BQN baja el QR ~10 dots)
  // y dejando a la derecha el ancho mínimo para el texto.
  const modulos = modulosQR(sinAcentos(qrData));
  const altoLibre = altoDots - pad - qrY - 10;
  const anchoLibre = w - (cfg.qrMinTextoDots || 85);
  const mag = Math.max(3, Math.min(cfg.qrMag || 7, Math.floor(Math.min(altoLibre, anchoLibre) / modulos)));
  out.push(`^FO${x},${qrY}^BQN,2,${mag}^FDLA,${esc(qrData)}^FS`);

  // El texto de la derecha arranca donde termina el QR (si no, se encima)
  const qrW = modulos * mag;
  const xr = x + qrW + 10; // origen del texto a la derecha del QR (deja zona libre al QR)
  const wr = ox + anchoDots - pad - xr; // ancho útil a la derecha
  let yr = qrY + 10;
  // Cada renglón calcula su tamaño para que SIEMPRE quepa en el ancho disponible.
  // El modelo puede usar 2 líneas (códigos como "10414 8879" no caben en una).
  const renglon = (texto, maxSize, maxLineas, alto) => {
    const s = tamanoQueCabe(texto, wr, maxLineas, maxSize);
    out.push(`^FO${xr},${yr}^A0N,${s},${Math.round(s * 0.85)}^FB${wr},${maxLineas},0,L,0^FD${esc(texto)}^FS`);
    yr += alto;
  };
  const modelo = reg.modelo || "";
  const lineasModelo = tamanoQueCabe(modelo, wr, 1, 30) >= 20 ? 1 : 2;
  renglon(modelo, 30, lineasModelo, lineasModelo === 1 ? 32 : 46);
  renglon(`Talla: ${reg.talla || ""}`, 20, 1, 24);
  // Con el QR más grande la columna es angosta: colores largos ("PALO DE ROSA") van en 2 líneas
  const color = `Color: ${reg.color || ""}`;
  const lineasColor = tamanoQueCabe(color, wr, 1, 18) >= 13 ? 1 : 2;
  renglon(color, 18, lineasColor, lineasColor === 1 ? 22 : 34);
  // Prenda de varias piezas (conjunto): cada pieza lleva el mismo QR y aquí dice cuál es
  // (con pieza, los renglones van un poco más juntos para que aún quepa el maquilero)
  if (reg.piezas > 1) renglon(`Pieza ${reg.pieza} de ${reg.piezas}`, 18, 1, 20);
  if (reg.corte) renglon(`Corte: ${reg.corte}`, 16, 1, reg.piezas > 1 ? 18 : 20);
  // El maquilero usa 2 líneas si cabe; si no, 1; y si ya no hay lugar sobre la marca del pie, se omite
  const restante = altoDots - 28 - yr;
  if (reg.maquilero && restante >= 16) renglon(`Maq: ${reg.maquilero}`, 15, restante >= 30 ? 2 : 1, 30);

  // === Marca al pie derecha ===
  out.push(`^FO${x},${altoDots - 24}^A0N,20,18^FB${w},1,0,R,0^FD${esc(M.marca)}^FS`);

  return out.join("");
}

// registros: [{ modelo, talla, qr?, composicion? }]  (uno por etiqueta a imprimir)
// configOverride: medidas del rollo (sobre-escribe CONFIG_ETIQUETA_ZEBRA)
export function generarZPL(registros, configOverride = {}) {
  const cfg = { ...CONFIG_ETIQUETA_ZEBRA, ...configOverride };
  const cols = cfg.columnas;
  const anchoDots = mm2dots(cfg.anchoEtiquetaMm, cfg.dpi);
  const altoDots = mm2dots(cfg.altoEtiquetaMm, cfg.dpi);
  const gapDots = mm2dots(cfg.gapHorizontalMm, cfg.dpi);
  const margenDots = mm2dots(cfg.margenMm, cfg.dpi);
  const rowWidthDots = margenDots + cols * anchoDots + (cols - 1) * gapDots;

  let zpl = "";
  for (let i = 0; i < registros.length; i += cols) {
    const fila = registros.slice(i, i + cols); // última fila puede traer <cols (resto en blanco)
    zpl += "^XA";
    // Sin ^CI28: el texto va sin acentos para que salga igual en Zebras viejas.
    if (cfg.oscuridad !== undefined && cfg.oscuridad !== null) {
      const sd = Math.max(0, Math.min(30, Number(cfg.oscuridad) || 0));
      zpl += `~SD${String(sd).padStart(2, "0")}`; // oscuridad absoluta (letra fina, menos calor)
    }
    if (cfg.velocidadIps) zpl += `^PR${cfg.velocidadIps},${cfg.velocidadIps},${cfg.velocidadIps}`;
    zpl += cfg.mediaConGap ? "^MNY" : "^MNN"; // gap/web sensing vs continuo
    zpl += "^LH0,0";                // origen en la esquina de la etiqueta
    zpl += `^PW${rowWidthDots}`;    // ancho total de la fila
    zpl += `^LL${altoDots}`;        // alto de la fila (un avance de papel)
    fila.forEach((reg, c) => {
      const ox = margenDots + c * (anchoDots + gapDots);
      zpl += columnaZPL(reg, ox, anchoDots, altoDots, cfg);
    });
    zpl += "^XZ";
  }
  return zpl;
}

// Fila de prueba con datos de ejemplo (para pegar en Labelary o mandar a la impresora).
export function zplPrueba(configOverride = {}) {
  const demo = [
    { modelo: "0004", talla: "UNICA", color: "NEGRO" },
    { modelo: "0004", talla: "UNICA", color: "NEGRO" },
    { modelo: "0004", talla: "UNICA", color: "NEGRO" },
  ];
  return generarZPL(demo, configOverride);
}
